/**
 * @file The library to check if a BISC Pack or a BISC Web Extension is installed and returns
 * a specific in case if it is not
 *
 * @module bisc-extension-checker
 * @version 5.0.0
 * @author Igor Bondarenko
 * @copyright Isabel Group NV, 2018
 */

// eslint-disable-next-line import/no-unresolved
import jBisc from 'jBisc';

const { detect } = require('detect-browser');

const defaultTimeout = 5000;

let factory;

/**
 * The status of BISC Pack Installation
 *
 * @readonly
 * @export
 * @enum {number}
*/
const biscExtnStatus = {
  EXTENSION_INSTALLED: 0,
  HTTP_BISC_INSTALLED: 1,
  ERROR_BROWSER_NOT_IN_SCOPE: -40,
  ERROR_CHROME_EXTENSION_NOT_INSTALLED: -41,
  ERROR_EDGE_CHROMIUM_EXTENSION_NOT_INSTALLED: -42,
  ERROR_FIREFOX_EXTENSION_NOT_INSTALLED: -44,
  ERROR_SAFARI_EXTENSION_NOT_INSTALLED: -45,
  ERROR_OS_TYPE_NOT_IN_SCOPE: -46,
  ERROR_WINDOWS_OS_SECURITY_PACK_NOT_INSTALLED: -47,
  ERROR_MAC_OS_SECURITY_PACK_NOT_INSTALLED: -48,
  UNEXPECTED_ERROR: -1,
};

/**
 * The different types of OS's
 *
 * @readonly
 * @private
 * @enum {number}
*/
const OSType = {
  WINDOWS: 'windows',
  MACOS: 'macos',
  UNSUPPORTED: 'unsupported',
};

/**
 * The different Browser
 *
 * @readonly
 * @private
 * @enum {number}
*/
const BrowserName = {
  CHROME: 'chrome',
  FIREFOX: 'firefox',
  EDGECHROMIUM: 'edge-chromium',
  SAFARI: 'safari',
};

/**
 * @private
 * @returns the current browser name.
 */
function getBrowserName() {
  const browser = detect();
  return (browser && browser.name) ? browser.name : '';
}

/**
 * Get the OS type
 *
 * @private
 * @return {Promise}. The promise will resolved with a status code
 */
function getOsType() {
  const platform = (navigator?.userAgentData?.platform || navigator?.platform || 'unknown').toLowerCase();

  if (platform.indexOf('win') !== -1) {
    return OSType.WINDOWS;
  }

  if (platform.indexOf('mac') !== -1) {
    return OSType.MACOS;
  }

  console.log(`[WEBExtChecker] Os type : ${platform}`);
  return OSType.UNSUPPORTED;
}

/**
 * Get a browser BISC Web Extension missing installation code.
 *
 * @private
 * @return an error code.
 */
function getBiscExtensionInstallationErrorCode() {
  const browserName = factory.getBrowserName();
  let code;
  switch (browserName) {
    case BrowserName.CHROME:
      code = biscExtnStatus.ERROR_CHROME_EXTENSION_NOT_INSTALLED;
      break;
    case BrowserName.FIREFOX:
      code = biscExtnStatus.ERROR_FIREFOX_EXTENSION_NOT_INSTALLED;
      break;
    case BrowserName.EDGECHROMIUM:
      code = biscExtnStatus.ERROR_EDGE_CHROMIUM_EXTENSION_NOT_INSTALLED;
      break;
    case BrowserName.SAFARI:
      code = biscExtnStatus.ERROR_SAFARI_EXTENSION_NOT_INSTALLED;
      break;
    default:
      code = biscExtnStatus.ERROR_BROWSER_NOT_IN_SCOPE;
  }
  return code;
}

/**
 * Get BISC Pack missing installation code.
 *
 * @private
 * @return an error code.
 */
function getBiscPackInstallationErrorCode() {
  const osName = factory.getOsType();
  if (osName === OSType.WINDOWS) {
    return biscExtnStatus.ERROR_WINDOWS_OS_SECURITY_PACK_NOT_INSTALLED;
  }

  if (osName === OSType.MACOS) {
    return biscExtnStatus.ERROR_MAC_OS_SECURITY_PACK_NOT_INSTALLED;
  }

  return biscExtnStatus.ERROR_OS_TYPE_NOT_IN_SCOPE;
}

/**
 * Check if a browser is supported by the BISC Pack.
 *
 * There are no requirements to check version for each browser.
 * For now check just the browser name field only.
 *
 * @private
 * @return true if a browser supports the BISC Pack Web extension, otherwise false
 */
function isBrowserInScope() {
  console.log('[WEBExtChecker] Checking the browser type ...');
  const browserName = getBrowserName();

  if (browserName !== BrowserName.CHROME
    && browserName !== BrowserName.EDGECHROMIUM
    && browserName !== BrowserName.FIREFOX
    && browserName !== BrowserName.SAFARI) {
    console.log(`[WEBExtChecker] the browser type '${browserName}' is not supported`);
    return false;
  }
  console.log(`[WEBExtChecker] the browser type '${browserName}' is supported`);
  return true;
}

/**
 * @private
 * @returns true if a BSIC extension is installed & enabled.
 */
async function isWebExtensionsEnabled() {
  console.log('[WEBExtChecker] Checking the Isabel-web-extension status ....');
  if (document.readyState === 'loading') {
    return new Promise((resolve) => {
      document.addEventListener('DOMContentLoaded', () => resolve(jBisc.isWebExtensionsEnabled()));
    });
  }
  const isEnabled = jBisc.isWebExtensionsEnabled();
  console.log(`[WEBExtChecker] the Isabel-web-extension is ${isEnabled ? 'enabled' : 'not enabled/installed'}`);
  return Promise.resolve(isEnabled);
}

async function isSecurityComponentsAvailable() {
  return jBisc.isSecurityComponentsAvailable(defaultTimeout);
}

function isBiscExeAvailable() {
  return factory.isSecurityComponentsAvailable()
    .then((status) => (status
      ? Promise.resolve(biscExtnStatus.EXTENSION_INSTALLED)
      : Promise.resolve(factory.getBiscPackInstallationErrorCode())))
    .catch((error) => {
      console.log(`[WEBExtChecker] Checking fails with : ${error}`);
      if (error.message && (error.message === '503' || error.message === 'ServiceUnavailable')) {
        console.log('[WEBExtChecker] Unable to detect a BISC WEBExtension Pack installation');
        return Promise.resolve(getBiscExtensionInstallationErrorCode());
      }
      if (error.message && error.message === '-22') {
        console.log('[WEBExtChecker] Unable to detect a BISC WEBExtension Pack installation');
        return Promise.resolve(getBiscPackInstallationErrorCode());
      }
      return Promise.resolve(error.message ? error.message : biscExtnStatus.UNEXPECTED_ERROR);
    });
}

/**
 * Check status of the BISC Pack installation
 *
 * @export
 * @return {Promise}. The promise will resolved with a status code
 */
function getBiscPackStatus() {
  // check BISC Pack presence
  console.log('[WEBExtChecker] Checking the BISC Pack availability ....');
  if (!factory.isBrowserInScope()) {
    return Promise.resolve(biscExtnStatus.ERROR_BROWSER_NOT_IN_SCOPE);
  }
  //
  return factory.isWebExtensionsEnabled()
    .then((status) => (status
      ? isBiscExeAvailable()
      : Promise.resolve(factory.getBiscExtensionInstallationErrorCode())));
}
//
factory = {
  // public
  biscExtnStatus,
  getBiscPackStatus,
  getLibraryVersion: () => '5.0.0',
  // private
  getBrowserName,
  getBiscExtensionInstallationErrorCode,
  getBiscPackInstallationErrorCode,
  getOsType,
  isBrowserInScope,
  isSecurityComponentsAvailable,
  isWebExtensionsEnabled,
};

export const biscExtensionChecker = factory;
export default biscExtensionChecker;
